import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AlbumLike } from "../types/album";
import { albumService } from "../services/albumService";
import { handleApiError, getErrorMessage } from "../utils/errorHandler";
import { authService } from "../services/authService";

export const albumLikeKeys = {
  all: ['albumLikes'] as const,
  lists: () => [...albumLikeKeys.all, 'list'] as const,
  list: (ownerId: string, albumId: string) => [...albumLikeKeys.lists(), ownerId, albumId] as const,
};

interface UseAlbumLikesReturn {
  likes: AlbumLike[];
  likesCount: number;
  currentUserLike: AlbumLike | null;
  isLikedByCurrentUser: boolean;
  isLoading: boolean;
  isToggling: boolean;
  error: string | null;
  toggleLike: () => Promise<void>;
  refreshLikes: () => Promise<void>;
  clearError: () => void;
}

export const useAlbumLikes = (
  ownerId: string | undefined,
  albumId: string | undefined,
  currentUserId: string | undefined
): UseAlbumLikesReturn => {
  const queryClient = useQueryClient();

  const {
    data: likes = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: albumLikeKeys.list(ownerId!, albumId!),
    queryFn: async () => {
      if (!ownerId || !albumId) {
        throw new Error("Missing ownerId or albumId");
      }
      await authService.checkAuthentication();
      return albumService.getAlbumLikes(ownerId, albumId);
    },
    enabled: !!ownerId && !!albumId,
  });

  const currentUserLike = likes.find(like => like.userId === currentUserId) || null;
  const isLikedByCurrentUser = currentUserLike !== null;
  const likesCount = likes.length;

  const createLikeMutation = useMutation({
    mutationFn: async () => {
      if (!ownerId || !albumId) {
        throw new Error("Missing ownerId or albumId");
      }
      await authService.checkAuthentication();
      return albumService.createAlbumLike(ownerId, albumId);
    },
    onSuccess: (newLike) => {
      queryClient.setQueryData<AlbumLike[]>(albumLikeKeys.list(ownerId!, albumId!), (old) => {
        return old ? [...old, newLike] : [newLike];
      });
    },
    onError: (error) => {
      console.error("Error creating album like:", handleApiError(error));
    },
  });

  const deleteLikeMutation = useMutation({
    mutationFn: async (likeId: string) => {
      if (!ownerId || !albumId) {
        throw new Error("Missing ownerId or albumId");
      }
      await authService.checkAuthentication();
      return albumService.deleteAlbumLike(ownerId, albumId, likeId);
    },
    onSuccess: (_, likeId) => {
      queryClient.setQueryData<AlbumLike[]>(albumLikeKeys.list(ownerId!, albumId!), (old) => {
        return old ? old.filter((like) => like.id !== likeId) : [];
      });
    },
    onError: (error) => {
      console.error("Error deleting album like:", handleApiError(error));
    },
  });

  const toggleLike = async () => {
    if (!ownerId || !albumId || !currentUserId) return;

    if (isLikedByCurrentUser && currentUserLike) {
      await deleteLikeMutation.mutateAsync(currentUserLike.id);
    } else {
      await createLikeMutation.mutateAsync();
    }
  };

  return {
    // Data
    likes,
    likesCount,
    currentUserLike,
    isLikedByCurrentUser,

    // Loading states
    isLoading,
    isToggling: createLikeMutation.isPending || deleteLikeMutation.isPending,

    // Error
    error: error ? getErrorMessage(handleApiError(error)) : null,

    // Actions
    toggleLike,
    refreshLikes: async () => { await refetch(); },
    clearError: () => {
      queryClient.resetQueries({ queryKey: albumLikeKeys.list(ownerId!, albumId!) });
    },
  };
};